export interface AnalyticsFilters {
  fromDay: string;
  toDay: string;
  currency?: string | undefined;
  productId?: string | undefined;
  competitorDomain?: string | undefined;
  outcome?: string | undefined;
  status?: string | undefined;
  reviewer?: string | undefined;
}

export interface AnalyticsFreshness {
  status: "FRESH" | "DELAYED" | "STALE" | "UNAVAILABLE";
  latestDataDay?: string;
  generatedAt: string;
  warning?: string;
}

export interface AnalyticsCurrencySegment {
  currency: string;
  requests: number;
  verified: number;
  reviewRequired: number;
  offers: number;
  accepted: number;
  ordered: number;
  recoveredRevenueMinor: number;
  discountCostMinor: number;
  refundedMinor: number;
  acceptanceRate: number;
  conversionRate: number;
}

export interface AnalyticsFunnelStep {
  id: string;
  label: string;
  value: number;
  href: string;
}

export interface AnalyticsDimension {
  id: string;
  label: string;
  value: number;
  href: string;
}

export interface AnalyticsDashboardDto {
  filters: AnalyticsFilters;
  marginVisible: boolean;
  empty: boolean;
  freshness: AnalyticsFreshness;
  currencySegments: AnalyticsCurrencySegment[];
  funnel: AnalyticsFunnelStep[];
  outcomes: AnalyticsDimension[];
  topProducts: AnalyticsDimension[];
  topCompetitors: AnalyticsDimension[];
  warnings: string[];
}

export interface AnalyticsTimeSeriesPoint {
  day: string;
  currency: string;
  recoveredRevenueMinor: number;
  discountCostMinor: number;
  ordered: number;
  requests: number;
}

export interface AnalyticsViewModel {
  dashboard: AnalyticsDashboardDto;
  timeseries: AnalyticsTimeSeriesPoint[];
  derived: AnalyticsDerivedMetrics;
}

export interface AnalyticsDerivedMetrics {
  automaticDecisionRate: number;
  manualDecisionRate: number;
  medianResponseMinutes?: number;
  medianReviewMinutes?: number;
  netRefundAdjustedRevenueMinor: number;
  grossMarginRetainedMinor?: number;
  invalidOrUnverifiableClaims: number;
  mappingHealthWarnings: number;
}

export function defaultAnalyticsFilters(): AnalyticsFilters {
  const toDay = new Date();
  const fromDay = new Date(toDay);
  fromDay.setUTCDate(fromDay.getUTCDate() - 29);
  return { fromDay: toIsoDay(fromDay), toDay: toIsoDay(toDay) };
}

export function deriveAnalyticsMetrics(dashboard: AnalyticsDashboardDto): AnalyticsDerivedMetrics {
  const segment = dashboard.currencySegments[0];
  const verified = segment?.verified ?? 0;
  const reviewRequired = segment?.reviewRequired ?? 0;
  const decisionTotal = verified + reviewRequired;
  const recovered = segment?.recoveredRevenueMinor ?? 0;
  const refunded = segment?.refundedMinor ?? 0;
  const discount = segment?.discountCostMinor ?? 0;
  return {
    automaticDecisionRate: decisionTotal === 0 ? 0 : verified / decisionTotal,
    manualDecisionRate: decisionTotal === 0 ? 0 : reviewRequired / decisionTotal,
    netRefundAdjustedRevenueMinor: recovered - refunded,
    ...(dashboard.marginVisible
      ? { grossMarginRetainedMinor: recovered - refunded - discount }
      : {}),
    invalidOrUnverifiableClaims: dashboard.outcomes
      .filter((item) => /reject|unverifiable|invalid/i.test(item.label))
      .reduce((sum, item) => sum + item.value, 0),
    mappingHealthWarnings: dashboard.warnings.filter((warning) =>
      /mapping|stale|fresh/i.test(warning),
    ).length,
  };
}

function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}
